// shared/utils/secureStorage.js
import logger from '../services/logging/logger';
import { asyncStorage } from '../services/storage/asyncStorageService';

let _CryptoJS = null;
const getCryptoJS = async () => {
  if (!_CryptoJS) _CryptoJS = (await import('crypto-js')).default;
  return _CryptoJS;
};

// Use environment-specific key (in production, this should come from env vars)
// IMPORTANT: In production, this MUST be set via REACT_APP_STORAGE_KEY environment variable
const ENCRYPTION_KEY = process.env.REACT_APP_STORAGE_KEY || 'tonic-dev-key-2026-change-in-prod';

/**
 * Secure storage utility for encrypting and safely accessing localStorage data
 * Provides AES-256 encryption for sensitive data and validation for all data
 *
 * Security Features:
 * - AES-256 encryption for sensitive fields (userData, roles, tokens, OTP sessions)
 * - Backward compatibility with unencrypted data (automatic migration)
 * - Validation and error handling
 */
class SecureStorage {
  /**
   * Encrypt data using AES-256
   * @private
   * @param {any} data - Data to encrypt
   * @returns {string} Encrypted string
   */
  async _encrypt(data) {
    try {
      const CryptoJS = await getCryptoJS();
      const encrypted = CryptoJS.AES.encrypt(JSON.stringify(data), ENCRYPTION_KEY).toString();
      return encrypted;
    } catch (error) {
      logger.error('Encryption failed', {
        error: error.message,
        method: '_encrypt'
      });
      throw error;
    }
  }

  /**
   * Decrypt data using AES-256
   * @private
   * @param {string} encryptedData - Encrypted string
   * @returns {any} Decrypted data
   */
  async _decrypt(encryptedData) {
    try {
      const CryptoJS = await getCryptoJS();
      const decrypted = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY);
      const decryptedStr = decrypted.toString(CryptoJS.enc.Utf8);

      if (!decryptedStr) {
        throw new Error('Decryption resulted in empty string');
      }

      return JSON.parse(decryptedStr);
    } catch (error) {
      logger.error('Decryption failed', {
        error: error.message,
        method: '_decrypt'
      });
      return null;
    }
  }

  /**
   * Store encrypted data
   * @param {string} key - Storage key
   * @param {any} value - Value to encrypt and store
   */
  async setEncrypted(key, value) {
    try {
      const encrypted = await this._encrypt(value);
      asyncStorage.set(key, encrypted);
      logger.debug('Data encrypted and stored', { key });
    } catch (error) {
      logger.error('Failed to store encrypted data', {
        key,
        error: error.message,
        method: 'setEncrypted'
      });
      throw error;
    }
  }

  /**
   * Store encrypted data synchronously (for cross-tab critical operations)
   * @param {string} key - Storage key
   * @param {any} value - Value to encrypt and store
   */
  async setEncryptedSync(key, value) {
    try {
      const encrypted = await this._encrypt(value);
      asyncStorage.setSync(key, encrypted);
      logger.debug('Data encrypted and stored synchronously', { key });
    } catch (error) {
      logger.error('Failed to store encrypted data synchronously', {
        key,
        error: error.message,
        method: 'setEncryptedSync'
      });
      throw error;
    }
  }

  /**
   * Retrieve and decrypt data
   * @param {string} key - Storage key
   * @param {any} fallback - Fallback value if decryption fails
   * @returns {any} Decrypted data or fallback
   */
  async getEncrypted(key, fallback = null) {
    try {
      const encrypted = asyncStorage.get(key);
      if (!encrypted) {
        return fallback;
      }

      // Try to decrypt
      const decrypted = await this._decrypt(encrypted);

      // If decryption fails, check if data is unencrypted (migration case)
      if (decrypted === null) {
        logger.warn('Decryption failed, attempting to read as unencrypted data', { key });
        try {
          const unencrypted = JSON.parse(encrypted);
          // If parsing succeeds, this is unencrypted data - migrate it
          logger.info('Found unencrypted data, migrating to encrypted storage', { key });
          await this.setEncrypted(key, unencrypted);
          return unencrypted;
        } catch {
          // Not valid JSON either - data is corrupted
          logger.error('Data is neither encrypted nor valid JSON', { key });
          return fallback;
        }
      }

      return decrypted;
    } catch (error) {
      logger.error('Failed to retrieve encrypted data', {
        key,
        error: error.message,
        method: 'getEncrypted'
      });
      return fallback;
    }
  }

  /**
   * Remove encrypted data
   * @param {string} key - Storage key
   */
  removeEncrypted(key) {
    try {
      asyncStorage.remove(key);
      logger.debug('Encrypted data removed', { key });
    } catch (error) {
      logger.error('Failed to remove encrypted data', {
        key,
        error: error.message,
        method: 'removeEncrypted'
      });
    }
  }

  /**
   * Store encrypted data in sessionStorage (tab-isolated).
   * Prevents cross-tab session bleed when multiple users are logged in simultaneously.
   * @param {string} key - Storage key
   * @param {any} value - Value to encrypt and store
   */
  async setEncryptedSession(key, value) {
    try {
      const encrypted = await this._encrypt(value);
      sessionStorage.setItem(key, encrypted);
      logger.debug('Data encrypted and stored in session storage', { key });
    } catch (error) {
      logger.error('Failed to store encrypted session data', {
        key,
        error: error.message,
        method: 'setEncryptedSession'
      });
      throw error;
    }
  }

  /**
   * Retrieve and decrypt data from sessionStorage.
   * @param {string} key - Storage key
   * @param {any} fallback - Fallback value if not found or decryption fails
   * @returns {any} Decrypted data or fallback
   */
  async getEncryptedSession(key, fallback = null) {
    try {
      const encrypted = sessionStorage.getItem(key);
      if (!encrypted) return fallback;
      const decrypted = await this._decrypt(encrypted);
      return decrypted !== null ? decrypted : fallback;
    } catch (error) {
      logger.error('Failed to retrieve encrypted session data', {
        key,
        error: error.message,
        method: 'getEncryptedSession'
      });
      return fallback;
    }
  }

  /**
   * Remove encrypted data from sessionStorage.
   * @param {string} key - Storage key
   */
  removeEncryptedSession(key) {
    try {
      sessionStorage.removeItem(key);
      logger.debug('Encrypted session data removed', { key });
    } catch (error) {
      logger.error('Failed to remove encrypted session data', {
        key,
        error: error.message,
        method: 'removeEncryptedSession'
      });
    }
  }

  /**
   * Safely retrieve user data from localStorage with validation
   * Now uses encrypted storage for security
   * @returns {Object|null} Parsed user data or null if invalid
   */
  getUserData() {
    try {
      // Try to get encrypted userData first
      const userData = this.getEncrypted('userData');

      if (!userData) {
        return null;
      }

      // Validate that it's an object
      if (typeof userData !== 'object' || userData === null) {
        logger.warn('Invalid userData format in localStorage');
        return null;
      }

      return userData;
    } catch (error) {
      logger.error('Failed to retrieve user data from storage', {
        error: error.message,
        method: 'getUserData'
      });
      return null;
    }
  }

  /**
   * Safely retrieve the tenant ID with validation.
   * @deprecated name kept for backward compatibility with existing callers — this actually
   * returns userData.tenantId, not a real Organisation ID.
   * @returns {string|null} Validated tenant ID or null
   */
  getOrganizationId() {
    try {
      const userData = this.getUserData();
      if (!userData) {
        return null;
      }

      const tenantId = userData.tenantId;

      // Validate tenant ID
      if (!tenantId || typeof tenantId !== 'string' || tenantId.trim().length === 0) {
        return null;
      }

      // Additional validation: must be alphanumeric, hyphen, or underscore
      // This prevents injection attacks
      if (!/^[a-zA-Z0-9_-]+$/.test(tenantId)) {
        logger.warn('Invalid tenant ID format detected', {
          method: 'getOrganizationId'
        });
        return null;
      }

      return tenantId;
    } catch (error) {
      logger.error('Failed to retrieve tenant ID', {
        error: error.message,
        method: 'getOrganizationId'
      });
      return null;
    }
  }

  /**
   * Safely retrieve user email with validation
   * @returns {string|null} Validated email or null
   */
  getUserEmail() {
    try {
      const userData = this.getUserData();
      if (!userData) {
        return null;
      }

      const email = userData.email;

      // Basic email validation
      if (!email || typeof email !== 'string') {
        return null;
      }

      // Validate email format (basic check)
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        logger.warn('Invalid email format detected', {
          method: 'getUserEmail'
        });
        return null;
      }

      return email;
    } catch (error) {
      logger.error('Failed to retrieve user email', {
        error: error.message,
        method: 'getUserEmail'
      });
      return null;
    }
  }

  /**
   * Safely retrieve user roles with validation
   * @returns {Array|null} Array of roles or null
   */
  getUserRoles() {
    try {
      const userData = this.getUserData();
      if (!userData) {
        return null;
      }

      const roles = userData.roles;

      // Validate roles is an array
      if (!Array.isArray(roles)) {
        return null;
      }

      return roles;
    } catch (error) {
      logger.error('Failed to retrieve user roles', {
        error: error.message,
        method: 'getUserRoles'
      });
      return null;
    }
  }

  /**
   * Store OTP session data securely (encrypted)
   * @param {string} email - User email
   * @param {string} otpSession - OTP session token
   */
  async setOTPSession(email, otpSession) {
    try {
      const otpData = {
        email,
        otpSession,
        timestamp: new Date().toISOString()
      };
      await this.setEncrypted('otp_session', otpData);
      logger.debug('OTP session stored securely');
    } catch (error) {
      logger.error('Failed to store OTP session', {
        error: error.message,
        method: 'setOTPSession'
      });
      throw error;
    }
  }

  /**
   * Retrieve OTP session data securely
   * @returns {Object|null} OTP session data or null
   */
  async getOTPSession() {
    try {
      const otpData = await this.getEncrypted('otp_session');
      if (!otpData) {
        return null;
      }

      // Validate structure
      if (!otpData.email || !otpData.otpSession) {
        logger.warn('Invalid OTP session data structure');
        this.removeEncrypted('otp_session');
        return null;
      }

      // Check if session is expired (15 minutes)
      if (otpData.timestamp) {
        const timestamp = new Date(otpData.timestamp);
        const now = new Date();
        const diffMinutes = (now - timestamp) / 1000 / 60;

        if (diffMinutes > 15) {
          logger.warn('OTP session expired');
          this.removeEncrypted('otp_session');
          return null;
        }
      }

      return otpData;
    } catch (error) {
      logger.error('Failed to retrieve OTP session', {
        error: error.message,
        method: 'getOTPSession'
      });
      return null;
    }
  }

  /**
   * Clear OTP session data
   */
  clearOTPSession() {
    try {
      this.removeEncrypted('otp_session');
      logger.debug('OTP session cleared');
    } catch (error) {
      logger.error('Failed to clear OTP session', {
        error: error.message,
        method: 'clearOTPSession'
      });
    }
  }
}

// Export singleton instance
export const secureStorage = new SecureStorage();

// Export class for testing
export { SecureStorage };
